import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import useWindowDimensions from "../../../services/useWindowDimensions";
import { TransactionDetailsType } from "../../../types/components";
import { CardTransaction, GetRRTransferDetailsResponse } from "../../../types/api/card";
import i18n from "../../../i18n/config";
import { API_FIELD_LABELS, RR_TRANSFER_METHODS, SUCCESS_FIELDS, TRANSACTION_SERVICE_TYPES, TRANSACTION_TYPES } from "../../../constants/enums";
import { GetApiData } from "../../../api/general";
import { ROUTES } from "../../../constants/api";
import { TransferDetailsType } from "../../../types/api/moneyTransfer";
import { DisplaySectionType } from "../../../types/components";
import useErrorModal from "../../../hooks/useErrorModal";

import Close from "../../../assets/images/icons/close.svg";
import CashPickupImg from "../../../assets/images/icons/cash-pickup.svg";
import BankImg from "../../../assets/images/icons/bank.svg";
import MobileWalletImg from "../../../assets/images/icons/mobile-wallet.svg";
import CreditCardUploadImg from "../../../assets/images/icons/credit-card-upload.svg";

import TransactionDetailsSkeletonLoader from "./TransactionDetailsSkeletonLoader";
import TransactionDetailsHeader from "./TransactionDetailsHeader";
import DisplaySection from "../../MoneyTransfer/DisplaySection";
import TransactionDetailsFooter from "./TransactionDetailsFooter";
import TransactionAvailableActions from "./TransactionAvailableActions";

const TransactionDetails = ({ transaction, fromClick, setButtonLoading }: { transaction: CardTransaction | null, fromClick: boolean, setButtonLoading: Dispatch<SetStateAction<boolean>> }) => {
  const { width } = useWindowDimensions();
  const [hide, setHide] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetailsType>();
  const [rrTransferMethod, setRRTransferMethod] = useState<string>();
  const openErrorModal = useErrorModal();
  const rrSDKTransactionTypes = [TRANSACTION_TYPES.MC_TO_ACCOUNT, TRANSACTION_TYPES.MOBILE_WALLET, TRANSACTION_TYPES.PUSH_TO_CARD];

  /**
   * Handle window resize events
   */
  useEffect(() => {
    if (width < 768) {
      setHide(true);
    } else {
      setHide(false);
    }
  }, [width]);

  /**
   * Handle transaction details fetching when transaction changes (i.e. user clicks different transaction to see details)
   */
  useEffect(() => {
    if (transaction) {
      handleGetTransactionDetails(transaction);

      if (fromClick && width < 768) {
        setHide(false);
        document.body.style.overflow = "hidden";
      }
    }
  }, [transaction, fromClick]);

  /**
   * Handle transaction details fetching
   */
  const handleGetTransactionDetails = async (transaction: CardTransaction) => {
    setLoading(true);
    let details: TransactionDetailsType;

    // if we have merchant data, we can show the transaction details
    if (transaction?.merchantData?.name) {
      details = mapMerchantDetailsFields(transaction);
    } else if (transaction?.transferId || transaction?.moneyTransferId) {
      let url = ROUTES.MONEY_TRANSFER.GET_TRANSFER_DETAILS(transaction.moneyTransferId);

      if (rrSDKTransactionTypes.includes(transaction.type)) {
        const transferMethod = getRRTransferMethodName(transaction?.type);
        setRRTransferMethod(transferMethod);
        url = ROUTES.CARD.GET_RR_SDK_TRANSFER_DETAILS(transaction?.transferId, transferMethod);
      } else if (transaction.type === TRANSACTION_TYPES.WU_TO_ACCOUNT) {
        url = ROUTES.MONEY_TRANSFER.GET_TRANSFER_DETAILS(transaction?.moneyTransferId, TRANSACTION_SERVICE_TYPES.TO_ACCOUNT);
      }

      const response = await getTransactionDetails(url);

      // The fetch failed and already surfaced the error modal - stop the loader and bail
      if (!response) {
        setLoading(false);
        return;
      }

      details = mapTransactionFields(response, transaction.formattedAmount, transaction);
    } else {
      details = mapGeneralDetailsFields(transaction);
    }

    setTransactionDetails(details);
    setLoading(false);
  };

  /**
   * Handle transaction details fetching from api 
   */
  const getTransactionDetails = async (url: string) => {
    try {
      return await GetApiData<TransferDetailsType | GetRRTransferDetailsResponse>(url);
    } catch (error) {
      openErrorModal(error);
      return undefined;
    }
  }

  /**
   * Map merchant details fields from transaction
   * @param transaction
   * @returns TransactionDetailsType
   */
  const mapMerchantDetailsFields = (transaction: CardTransaction): TransactionDetailsType => {
    const dateObj = new Date(transaction.date);
    const dateStr = dateObj.toLocaleDateString(window.navigator.language, { month: "long", day: "numeric", year: "numeric" });
    const timeStr = dateObj.toLocaleTimeString(window.navigator.language, { hour: "numeric", minute: "numeric" });

    // Set initial display fields to contain date, time, merchant name
    // Then conditionally add category, and finally add address at the end
    const displayFields = [
      {
        label: i18n.t("COMMON.DATE"),
        formattedValue: dateStr,
      },
      {
        label: i18n.t("COMMON.TIME"),
        formattedValue: timeStr,
      },
      {
        label: i18n.t("COMMON.MERCHANT"),
        formattedValue: transaction.merchantData?.name ?? "",
      }
    ]

    if (transaction.merchantData?.category?.description) {
      displayFields.push({
        label: i18n.t("COMMON.CATEGORY"),
        formattedValue: transaction.merchantData.category.description,
      });
    }

    displayFields.push({
      label: i18n.t("COMMON.ADDRESS"),
      formattedValue: transaction.merchantData?.address?.formattedAddress ?? ""
    });

    return {
      logo: transaction?.logo.url,
      formattedAmount: transaction.formattedAmount,
      displayFields
    }
  }

  /**
   * Map ODT, Wage transaction details fields from transaction
   * @param transaction
   * @returns TransactionDetailsType
   */
  const mapGeneralDetailsFields = (transaction: CardTransaction): TransactionDetailsType => {
    const dateObj = new Date(transaction.date);
    const dateStr = dateObj.toLocaleDateString(window.navigator.language, { month: "long", day: "numeric", year: "numeric" });
    const timeStr = dateObj.toLocaleTimeString(window.navigator.language, { hour: "numeric", minute: "numeric" });
    const descriptionStr = transaction.description;

    return {
      logo: transaction.logo.url,
      formattedAmount: transaction.formattedAmount,
      displayFields: [
        {
          label: i18n.t("COMMON.DATE"),
          formattedValue: dateStr,
        },
        {
          label: i18n.t("COMMON.TIME"),
          formattedValue: timeStr,
        },
        {
          label: i18n.t("COMMON.DESCRIPTION"),
          formattedValue: descriptionStr,
        }
      ],
    }
  }

  /**
   * Map transaction fields from API response
   * @param response
   * @param formattedAmount
   * @returns TransactionDetailsType
   */
  const mapTransactionFields = (response: TransferDetailsType | GetRRTransferDetailsResponse, formattedAmount: string, transaction: CardTransaction): TransactionDetailsType => {
    const displayFields: NonNullable<TransactionDetailsType["displayFields"]> = [];
    const displaySections: DisplaySectionType[] = [];
    let logo = "displayFields" in response ? response.logo : CashPickupImg;
    let referenceNumberField: { label: string; formattedValue: string } | undefined;

    // Cash Pickup and WU2A have displayFields property
    if ("displayFields" in response) {
      response.displayFields.forEach(field => {
        if (field.id !== SUCCESS_FIELDS.REFERENCE_NUMBER) {
          displayFields.push({
            id: field.id,
            label: field.label,
            formattedValue: field.formattedValue
          });
        } else {
          referenceNumberField = {
            label: field.label,
            formattedValue: field.formattedValue
          };
        }
      });
    } else {
      logo = getTransactionTypeImage(transaction.type);
      response.result.displaySections.forEach(section => {
        displaySections.push({
          name: section.name,
          id: section.id,
          label: section.label,
          fields: section.fields.map(field => {
            if (field.label === API_FIELD_LABELS.REFERENCE_NUMBER) {
              referenceNumberField = {
                label: field.label,
                formattedValue: field.formattedValue
              };
            }
            return field;
          })
        });
      });
    }

    return {
      logo,
      formattedAmount: formattedAmount,
      downloadReceiptText: "displayFields" in response ? response.downloadReceiptText : response.result.downloadReceiptText,
      downloadReceiptUrl: "displayFields" in response ? response.downloadReceiptUrl : response.result.downloadReceiptUrl,
      moneyTransferId: "displayFields" in response ? response.moneyTransferId : undefined,
      referenceNumberField,
      pickupInstructions: "displayFields" in response && response?.pickupInstructions.length > 0 ? response.pickupInstructions : undefined,
      disclaimers: "displayFields" in response ? response.disclaimers : response.result.footer.disclaimers,
      footerContent: "displayFields" in response ? undefined : response.result.footer?.content,
      availableActions: "displayFields" in response ? response?.availableActions : undefined,
      displayFields: displayFields.length > 0 ? displayFields : undefined,
      displaySections: displaySections.length > 0 ? displaySections : undefined
    }
  }

  /**
   * Get the transfer method name based on the transaction type
   * @param type
   * @returns Transfer method name
   */
  const getRRTransferMethodName = (type: number) => {
    switch (type) {
      case TRANSACTION_TYPES.PUSH_TO_CARD:
        return RR_TRANSFER_METHODS.PUSH_TO_CARD;
      case TRANSACTION_TYPES.MOBILE_WALLET:
        return RR_TRANSFER_METHODS.MOBILE_WALLET;
      case TRANSACTION_TYPES.MC_TO_ACCOUNT:
        return RR_TRANSFER_METHODS.SEND_WITH_BRIGHTWELL;
    }
  }

  /**
   * handle close click event
   */
  const handleCloseClick = () => {
    setHide(true);
    document.body.style.overflow = "auto";
  }

  const getTransactionTypeImage = (transactionType: number) => {
    switch (transactionType) {
      case TRANSACTION_TYPES.ODT:
      case TRANSACTION_TYPES.MC_TO_ACCOUNT:
        return BankImg;
      case TRANSACTION_TYPES.MOBILE_WALLET:
        return MobileWalletImg;
      case TRANSACTION_TYPES.PUSH_TO_CARD:
        return CreditCardUploadImg;
      case TRANSACTION_TYPES.MONEY_TRANSFER:
      default:
        return CashPickupImg;
    }
  }

  return (
    <section className={`transaction-details-container ${hide ? "hide" : ""}`}>
      <div className="relative">
        <h2>{i18n.t("PAGES.MY_CARD.TRANSACTIONS.TRANSACTION_DETAILS")}</h2>
        {width < 768 &&
          <button onClick={handleCloseClick} className="no-styling absolute absolute__top-right">
            <img src={Close} alt="Close" />
          </button>
        }
      </div>
      <div className="bg-color-white details-container display-section-wrapper">

        <TransactionDetailsSkeletonLoader loading={loading} />

        {!loading && transaction && transactionDetails &&
          <>
            {transactionDetails.availableActions &&
              <TransactionAvailableActions
                transactionDetails={transactionDetails}
                handleGetTransactionDetails={() => handleGetTransactionDetails(transaction)}
                moneyTransferId={transaction.moneyTransferId}
              />
            }
            <TransactionDetailsHeader transactionDetails={transactionDetails} />

            {transactionDetails?.displayFields &&
              <ul className="transaction-details-list">
                {transactionDetails?.displayFields.map((field) => (
                  <li key={field.label} className="transaction-details-list-item">
                    <p>{field.label}</p>
                    {field.label === i18n.t("COMMON.DESCRIPTION")
                      ? <p dangerouslySetInnerHTML={{ __html: field.formattedValue }} />
                      : <p>{field.formattedValue}</p>
                    }
                  </li>
                ))}
              </ul>
            }

            {transactionDetails?.displaySections &&
              <DisplaySection sections={transactionDetails.displaySections} variant="transparent" className="spacing-top-small" />
            }

            <TransactionDetailsFooter
              transactionDetails={transactionDetails}
              transferId={transaction.transferId}
              transactionType={transaction.type}
              rrTransferMethod={rrTransferMethod}
              setButtonLoading={setButtonLoading}
            />
          </>
        }
      </div>
    </section>
  );
}

export default TransactionDetails;